import React, {useEffect, useMemo, useState} from "react";
import {Rive} from "@rive-app/react-canvas-lite";
import {User, PlacementTestInfo, WithLoading} from "shared/types/VaultTypes";
import {SceneDialog} from "../../core/view/SceneDialog";
import * as styles from "./styles/PageOnboarding.module.css";
import ColorUtil from "../util/ColorUtil";
import {
	FLY_AWAY_TRIGGER,
	getMixpanelDataFromUser,
	getOnePlacementUrl,
	getPlacementLevelAndChapter,
	LOOK_DOWN_TRIGGER,
	LOOK_UP_TRIGGER,
	OnboardingState,
	riveAnimateDoorOpening,
} from "./util/PageOnboardingUtil";
import {track} from "../util/Mixpanel.js";
import OpsUser from "../vault/OpsUser";
import {BALevelCarousel} from "src/parent/primaryRegistration/BALevelCarouselV2";
import {
	AGE_RANGES_BY_LEVEL,
	LEVEL_DESCRIPTIONS,
} from "src/parent/util/PrimaryRegistrationUtilV2";
import {AT} from "../vault/ActionTypeConstants";

const WonkyButton = require("../view/WonkyButton");

export type OnboardingStepProps = {
	displayName: string;
	user: User;
	dispatch: (action: any) => void;
	rive: Rive | null;
	placementTestInfo: PlacementTestInfo & WithLoading;
	onboardingState: OnboardingState;
	setOnboardingState: React.Dispatch<React.SetStateAction<OnboardingState>>;
};

const BaseOnboardingStep = ({
	dialog,
	children: buttons,
	isDialogHidden,
}: {
	dialog: React.ReactElement | string;
	children: React.ReactElement;
	isDialogHidden?: boolean;
}) => {
	return (
		<SceneDialog
			tabLabel="Headmaster"
			className={
				isDialogHidden
					? `${styles.sceneDialogWrapper} ${styles.hiddenDialog}`
					: styles.sceneDialogWrapper
			}
		>
			{typeof dialog === "string" ? (
				<p className={styles.dialogTitle}>{dialog}</p>
			) : (
				dialog
			)}
			<SceneDialog.Footer>{buttons}</SceneDialog.Footer>
		</SceneDialog>
	);
};

export const OnboardingStepNotStarted = ({
	displayName,
	user,
	dispatch,
}: OnboardingStepProps) => {
	const placementTestUrl = useMemo(() => getOnePlacementUrl(user), [user]);
	return (
		<BaseOnboardingStep
			dialog={`Alright ${displayName}, it's time to take your placement test!`}
		>
			<WonkyButton
				buttonText={"NEXT"}
				buttonType="trapNormal"
				linkTo={placementTestUrl}
				onLinkClick={() => {
					track("Student Onboarding: Click Link", {
						step: "NOT_STARTED",
						...getMixpanelDataFromUser(user),
					});
					dispatch({type: AT.FORCE_LOAD_USER_PENDING});
				}}
				isExternal={true}
				tabIndex={0}
			/>
		</BaseOnboardingStep>
	);
};

export const OnboardingStepInProgress = ({
	displayName,
	user,
	setOnboardingState,
	dispatch,
	rive,
}: OnboardingStepProps) => {
	const placementTestUrl = useMemo(() => getOnePlacementUrl(user), [user]);
	return (
		<BaseOnboardingStep
			dialog={`Welcome back ${displayName}! Are you ready to finish your test?`}
		>
			<>
				<WonkyButton
					buttonText={"CHOOSE LEVEL"}
					buttonType="trapFlipped"
					handleClick={() => {
						track("Student Onboarding: Click Next", {
							step: "IN_PROGRESS",
							...getMixpanelDataFromUser(user),
						});
						setOnboardingState({step: "CHOOSE_A_LEVEL"});
						rive?.viewModelInstance?.trigger(LOOK_DOWN_TRIGGER)?.trigger();
					}}
					variant="outline"
					tabIndex={0}
				/>
				<WonkyButton
					buttonText={"FINISH TEST"}
					buttonType="trapNormal"
					linkTo={placementTestUrl}
					onLinkClick={() => {
						track("Student Onboarding: Click Link", {
							step: "IN_PROGRESS",
							...getMixpanelDataFromUser(user),
						});
						dispatch({type: AT.FORCE_LOAD_USER_PENDING});
					}}
					isExternal={true}
					tabIndex={0}
				/>
			</>
		</BaseOnboardingStep>
	);
};

export const OnboardingStepPlacementTestLoadError = ({
	setOnboardingState,
	rive,
	user,
}: OnboardingStepProps) => {
	const [isDialogHidden, setIsDialogHidden] = useState(false);
	return (
		<BaseOnboardingStep
			dialog={`The placement test isn't available right now. We'll prompt you when it's back. Meanwhile, let's see where you'll start...`}
			isDialogHidden={isDialogHidden}
		>
			<WonkyButton
				buttonText={"NEXT"}
				buttonType="trapNormal"
				handleClick={() => {
					track("Student Onboarding: Click Next", {
						step: "PLACEMENT_TEST_LOAD_ERROR",
						...getMixpanelDataFromUser(user),
					});
					setIsDialogHidden(true);
					riveAnimateDoorOpening(
						rive,
						"PLACEMENT_TEST_LOAD_ERROR",
						setOnboardingState
					);
				}}
				tabIndex={0}
			/>
		</BaseOnboardingStep>
	);
};

export const OnboardingStepChooseALevel = ({
	displayName,
	user,
	rive,
	setOnboardingState,
	placementTestInfo,
}: OnboardingStepProps) => {
	const suggestedLevel = useMemo(() => OpsUser.getUserGrade(user), [user]);
	const [selectedLevel, setSelectedLevel] = useState<number | undefined>(
		OpsUser.getUserGrade(user)
	);
	const [isDialogHidden, setIsDialogHidden] = useState(false);

	return (
		<BaseOnboardingStep
			isDialogHidden={isDialogHidden}
			dialog={
				<>
					<p style={{margin: "0 0 24px"}}>
						<b>Level {suggestedLevel}</b> is a good place to start, based on
						your age.
					</p>

					<BALevelCarousel
						selectedLevel={selectedLevel}
						onLevelSelect={setSelectedLevel}
						levels={[1, 2, 3, 4, 5].map(level => ({
							description: LEVEL_DESCRIPTIONS[level].replace(
								"{name}",
								displayName
							),
							recommendedAges: AGE_RANGES_BY_LEVEL[level],
						}))}
					/>

					<p style={{margin: "12px 0 0", fontSize: "0.875rem", color: "#888"}}>
						Your Parent can change this at any time from their Account.
					</p>
				</>
			}
		>
			<>
				<WonkyButton
					buttonText={"Back"}
					buttonType="trapFlipped"
					handleClick={() => {
						track("Student Onboarding: Click Back", {
							step: "CHOOSE_A_LEVEL",
							...getMixpanelDataFromUser(user),
						});
						setOnboardingState({
							step: "IN_PROGRESS",
						});
						rive?.viewModelInstance?.trigger(LOOK_UP_TRIGGER)?.trigger();
					}}
					variant="outline"
					tabIndex={0}
				/>
				<WonkyButton
					buttonText={"Next"}
					buttonType="trapNormal"
					handleClick={() => {
						track("Student Onboarding: Click Next", {
							step: "CHOOSE_A_LEVEL",
							selectedLevel,
							...getMixpanelDataFromUser(user),
						});
						setIsDialogHidden(true);
						riveAnimateDoorOpening(
							rive,
							"CHOOSE_A_LEVEL",
							setOnboardingState,
							selectedLevel
						);
					}}
					tabIndex={0}
				/>
			</>
		</BaseOnboardingStep>
	);
};

export const OnboardingStepTestComplete = ({
	setOnboardingState,
	rive,
	user,
}: OnboardingStepProps) => {
	const [isDialogHidden, setIsDialogHidden] = useState(false);
	return (
		<BaseOnboardingStep
			dialog={"Test complete! Let's see where you've landed..."}
			isDialogHidden={isDialogHidden}
		>
			<WonkyButton
				buttonText={"NEXT"}
				buttonType="trapNormal"
				handleClick={() => {
					track("Student Onboarding: Click Next", {
						step: "TEST_COMPLETE",
						...getMixpanelDataFromUser(user),
					});
					setIsDialogHidden(true);
					riveAnimateDoorOpening(rive, "TEST_COMPLETE", setOnboardingState);
				}}
				tabIndex={0}
			/>
		</BaseOnboardingStep>
	);
};

export const OnboardingStepParentManuallySelectedLevel = ({
	setOnboardingState,
	rive,
	user,
	displayName,
}: OnboardingStepProps) => {
	const [isDialogHidden, setIsDialogHidden] = useState(false);
	return (
		<BaseOnboardingStep
			dialog={`Alright ${displayName}, let's see where you'll start...`}
			isDialogHidden={isDialogHidden}
		>
			<WonkyButton
				buttonText={"NEXT"}
				buttonType="trapNormal"
				handleClick={() => {
					track("Student Onboarding: Click Next", {
						step: "PARENT_MANUALLY_SELECTED_LEVEL",
						...getMixpanelDataFromUser(user),
					});
					setIsDialogHidden(true);
					riveAnimateDoorOpening(
						rive,
						"PARENT_MANUALLY_SELECTED_LEVEL",
						setOnboardingState,
						parseInt(user.model!.gradeID.toString())
					);
				}}
				tabIndex={0}
			/>
		</BaseOnboardingStep>
	);
};

export const OnboardingStepReadyToStart = ({
	dispatch,
	onboardingState,
	placementTestInfo,
	user,
	rive,
}: OnboardingStepProps) => {
	const [isDialogHidden, setIsDialogHidden] = useState(true);
	const levelPlacement =
		onboardingState.manualLevelSelection ??
		getPlacementLevelAndChapter(placementTestInfo, user);
	const levelName = `Level ${
		levelPlacement.level
	}, Chapter ${levelPlacement.chapterIndex + 1}`;
	const levelNameElement = (
		<span style={{color: ColorUtil.getCurrentScheme().blue}}>{levelName}</span>
	);
	let headmasterDialogElement = (
		<p className={styles.dialogTitle}>
			You'll start in {levelNameElement}! Ready to start?
		</p>
	);
	if (placementTestInfo.load === "error") {
		headmasterDialogElement = (
			<p className={styles.dialogTitle}>
				You'll start in {levelNameElement}. Your parent can change this at any
				time. Ready to start?
			</p>
		);
	}
	useEffect(() => {
		// Unhide it after a frame, so that the CSS transition will actually take place.
		// TODO(jaketrower): Probably better to just use React.CSSTransition for this...
		requestAnimationFrame(() => {
			setIsDialogHidden(false);
		});
	}, []);
	return (
		<BaseOnboardingStep
			dialog={headmasterDialogElement}
			isDialogHidden={isDialogHidden}
		>
			<WonkyButton
				buttonText={"YES, LET'S GO!"}
				buttonType="trapNormal"
				handleClick={() => {
					setIsDialogHidden(true);
					rive?.viewModelInstance?.trigger(FLY_AWAY_TRIGGER)?.trigger();
					track("Student Onboarding: Click Complete", {
						step: "READY_TO_START",
						...getMixpanelDataFromUser(user),
					});
					if (placementTestInfo.load === "error") {
						dispatch({
							type: AT.UPDATE_USER_EVENTS_TRIGGERED,
							pathArray: ["notifications", "studentProfilePlacementTest"],
							propValue: "needs-to-take",
						});
					}
					setTimeout(() => {
						dispatch({
							type: AT.STUDENT_ONBOARDING_PLACEMENT_COMPLETE_REQUEST,
							payload: {
								grade: levelPlacement.level,
								chapterIndex: levelPlacement.chapterIndex,
							},
						});
					}, 2000);
				}}
				tabIndex={0}
			/>
		</BaseOnboardingStep>
	);
};
